import { Box, Stack, InputGroup, InputLeftElement, Icon, Input, HStack, Divider, Button, IconButton, Menu, MenuButton, MenuList, Container, MenuItem, useToast } from '@chakra-ui/react'
import { FiMenu, FiRefreshCw, FiSearch, FiEye } from 'react-icons/fi'
import { CourseTable } from './CourseTable'
import React from 'react'
import { useNavigate } from 'react-router-dom'
import { ICourse } from '@/shared/ICourse'
import { CheckboxFilterStepStatus } from './filters/CheckboxFilterStepStatus'
import { TelemetryClientService } from '../services/TelemetryClientService'
import { CheckboxFilterComments } from './filters/CheckboxFilterComments'
import { CommentsService } from '../services/CommentsService'
import { AuthService } from '../services/AuthService'
import { CheckboxFilterProvider } from './filters/CheckboxFilterProvider'
import { CheckboxFilterExpert } from './filters/CheckboxFilterExpert'
import { CheckboxFilterYear } from './filters/CheckboxFilterYear'
import { NewCourseDialog } from './NewCourseDialog'
import { AppContext } from '../comments/AppContext'
import { CourseStorageService } from '../services/CourseStorageService'
import { MenuItemOpenSharePoint } from '../course/MenuItemOpenSharePoint'
import { CheckboxFilterSegment } from './filters/CheckboxFilterSegment'
import { DefaultCourseSegment } from './filters/CourseSegmentOptions'
import { CheckboxFilterLabel } from './filters/CheckboxFilterLabel'
import { CheckboxFilterVersion } from './filters/CheckboxFilterVersion'
import { CheckboxFilterArchived } from './filters/CheckboxFilterArchived'
import { useFilterText } from './filters/useFilterText'
import { EditCourseDialog } from './EditCourseDialog'
import { DeleteCourseDialog } from '../course/DeleteCourseDialog'
import { AssignToExpertDialog } from '../review/AssignToExpertDialog'
import { NewRepeatedCourseDialog } from './NewRepeatedCourseDialog'
import { RemindProviderDialog } from './RemindProviderDialog'
import { EditDeadlineDialog } from '../deadlines/EditDeadlineDialog'
import { AWARD_STATUS, PLATFORM_VERSION, COMMENT_STATUS } from '@/shared/constants'
import { ColumnVisibilitySidebar } from './ColumnVisibilitySidebar'
import { VisibilityState, ColumnOrderState } from '@tanstack/react-table'
import { useLocalStorageState } from '../components/useLocalStorageState'
import { CourseColumnDefinitions, defaultColumnVisibility, defaultColumnOrder } from './CourseColumnDefinitions'
import { AppToolbar } from '../navbar/AppToolbar';
import { FilterBox } from './FilterBox'
import { TableCounterBox } from './TableCounterBox'
import { IUserInfo } from '@/shared/IUserInfo'
import { stringifyError } from '@/shared/parse'

export const CourseTableFrame = (props: {
  errorMessage?: any, setErrorMessage: (error: any) => void,
}) => {

  TelemetryClientService.page('CourseTable');
  const [filterText, setFilterText, filterWords] = useFilterText('eitum-course-table-filter-text');

  const [columnVisibility, setColumnVisibility] = useLocalStorageState<VisibilityState>('eitum-course-table-column-visibility', defaultColumnVisibility);
  const [columnOrder, setColumnOrder] = useLocalStorageState<ColumnOrderState>('eitum-course-table-column-order', defaultColumnOrder);
  const [isColumnSidebarOpen, setIsColumnSidebarOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);

  const [newCoursePlatform, setNewCoursePlatform] = React.useState<PLATFORM_VERSION>();
  const [filterStep, setFilterStep] = useLocalStorageState<string[] | undefined>('eitum-course-table-filter-step', undefined);
  const [filterLabel, setFilterLabel] = useLocalStorageState<string[] | undefined>('eitum-course-table-filter-label', undefined);
  const [filterVersion, setFilterVersion] = useLocalStorageState<string[] | undefined>('eitum-course-table-filter-version', undefined);
  const [fitlerComment, setFilterComment] = useLocalStorageState<string[] | undefined>('eitum-course-table-filter-comment', undefined);
  const [filterProviderId, setFilterProviderId] = useLocalStorageState<string[] | undefined>('eitum-course-table-filter-provider', undefined);
  const [filterExpertId, setFilterExpertId] = useLocalStorageState<string[] | undefined>('eitum-course-table-filter-expert', undefined);
  const [filterSegment, setFilterSegment] = useLocalStorageState<string[] | undefined>('eitum-course-table-filter-segment', undefined);
  const [filterArchived, setFilterArchived] = useLocalStorageState<string[] | undefined>('eitum-course-table-filter-archived', undefined);
  const [filterYear, setFilterYear] = useLocalStorageState<string[] | undefined>('eitum-course-table-filter-year', undefined);

  const { isAuthenticated, updateRoles } = React.useContext(AppContext);

  const [allCourses, setAllCourses] = React.useState<ICourse[]>([]);

  // Cache searchable text for each course to avoid repeated computations
  const coursesWithSearchCache = React.useMemo(() => {
    return allCourses.map(course => ({
      ...course,
      _searchText: [
        course.folderName,
        course.CourseTitle,
        course.CourseStep,
        course.expert?.email,
        course.expert?.name,
        ...(course.CourseProviderUsers?.flatMap(u => [u.email, u.name]) ?? []),
        ...(course.CourseStaffUsers?.flatMap(u => [u.email, u.name]) ?? []),
      ].filter(Boolean).join(' ').toLowerCase()
    }));
  }, [allCourses]);

  const navigate = useNavigate();

  const onCourseDeleted = (course: ICourse) => {
    setAllCourses(courses => courses.filter(c => c.id !== course.id));
    setCourseToDelete(undefined);
  }
  const onCourseUpdated = (course: ICourse) => {
    setAllCourses(courses => courses.map(c => c.id === course.id ? course : c));
    setCourseToUpdate(undefined);
  }

  const onRepeatedCourseCreated = (course: ICourse) => {
    setAllCourses(courses => [...courses, course]);
    setCourseToCreateNewRepeated(undefined);
    navigate(`/course/${course.id}`);
  }
  const onCourseExpertInvited = async (course: ICourse) => {
    setAllCourses(courses => courses.map(c => c.id === course.id ? course : c));
    setCourseToInviteExpert(undefined);
  }

  const onCourseArchived = async (course: ICourse) => {
    const isArchiving = !course.archived;
    toast.promise(
      CourseStorageService.patchCourse(course.id, { archived: isArchiving }).then(updated => {
        setAllCourses(courses => courses.map(c => c.id === course.id ? updated : c));
        return updated;
      }),
      {
        loading: { title: `${isArchiving ? 'Archiving' : 'Unarchiving'} course...` },
        success: (updated) => ({ 
          title: updated.archived ? 'Course archived' : 'Course unarchived',
          description: updated.archived ? 'The course has been archived successfully' : 'The course has been unarchived successfully'
        }),
        error: err => ({ 
          title: `Failed to ${isArchiving ? 'archive' : 'unarchive'} course`,
          description: stringifyError(err) 
        }),
      }
    );
  }

  const loadCourses = async (force?: boolean) => {

    setIsLoading(true);
    props.setErrorMessage(undefined);
    try {
      const data = await CourseStorageService.getCourses(force);
      updateRoles(data.roles);
      setAllCourses(data.courses);
    } catch (error) {
      props.setErrorMessage(error);
      return [];
    } finally {
      setIsLoading(false);
    }
  }
  React.useEffect(() => {
    if (isAuthenticated) {
      loadCourses(false);
    }
  }, [isAuthenticated]);

  const toast = useToast();
  const onSendReminder = async (course: ICourse, user: IUserInfo, message: string) => {
    await CourseStorageService.remindProvider(course.id, user, message);
    toast({
      title: 'Notification sent',
      description: `Message sent to ${user.name} (${user.email})`,
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
    setCourseToRemindProvider(undefined);
  }

  const [courseToEdit, setCourseToUpdate] = React.useState<ICourse>();
  const [courseToDelete, setCourseToDelete] = React.useState<ICourse>();
  const [courseToCreateNewRepeated, setCourseToCreateNewRepeated] = React.useState<ICourse>();
  const [courseToInviteExpert, setCourseToInviteExpert] = React.useState<ICourse>();
  const [courseToRemindProvider, setCourseToRemindProvider] = React.useState<ICourse>();
  const [courseToEditDeadlines, setCourseToEditDeadlines] = React.useState<ICourse>();

  // Single filter function that handles both cases
  const getFilteredCourses = React.useCallback((excludeFilter?: string) => {
    if (!coursesWithSearchCache) {
      return [];
    }
    return coursesWithSearchCache.filter(c => {

      if (excludeFilter !== 'step' && filterStep?.length && filterStep.every(x => c.CourseStep !== x)) {
        return false;
      }

      if (excludeFilter !== 'label' && filterLabel?.length && filterLabel.every(x => (c.LabelAwarded || AWARD_STATUS.UNKNONWN) !== (x || AWARD_STATUS.UNKNONWN))) {
        return false;
      }

      if (excludeFilter !== 'version' && filterVersion?.length && filterVersion.every(x => (c.coursePlatformVersion || PLATFORM_VERSION.V1) !== x)) {
        return false;
      }

      if (excludeFilter !== 'provider' && filterProviderId?.length && filterProviderId.every(x => c.providerId !== x)) {
        return false;
      }

      if (excludeFilter !== 'expert' && filterExpertId?.length && filterExpertId.every(x => c.expertId !== x)) {
        return false;
      }

      if (excludeFilter !== 'comment' && fitlerComment?.length && !fitlerComment.some(x => CommentsService.getQuestionCommentCount(c.commentSet, comment => (comment.status || COMMENT_STATUS.COMMENT) === x) > 0)) {
        return false;
      }

      if (excludeFilter !== 'segment' && filterSegment?.length && filterSegment.every(x => (c.CourseSegment || DefaultCourseSegment) !== x)) {
        return false;
      }

      if (excludeFilter !== 'archived' && filterArchived?.length) {
        const isArchived = c.archived || false;
        const shouldShow = filterArchived.some(x => 
          (x === 'archived' && isArchived) || 
          (x === 'active' && !isArchived)
        );
        if (!shouldShow) {
          return false;
        }
      }

      if (excludeFilter !== 'year' && filterYear?.length) {
        const yearMatch = c.folderName?.match(/20\d{2}/);
        const courseYear = yearMatch ? yearMatch[0] : null;
        if (!courseYear || !filterYear.includes(courseYear)) {
          return false;
        }
      }

      if (filterWords.length > 0) {
        return filterWords.every(w => c._searchText.includes(w));
      }

      return true;
    });
  }, [coursesWithSearchCache, filterWords, filterStep, fitlerComment, filterProviderId, filterExpertId, filterSegment, filterLabel, filterVersion, filterArchived, filterYear]);

  // Main filtered courses (all filters applied)
  const courses = React.useMemo(() => getFilteredCourses(), [getFilteredCourses]);

  const coursesForYearFilter = React.useMemo(() => getFilteredCourses('year'), [getFilteredCourses]);
  const coursesForProviderFilter = React.useMemo(() => getFilteredCourses('provider'), [getFilteredCourses]);
  const coursesForExpertFilter = React.useMemo(() => getFilteredCourses('expert'), [getFilteredCourses]);
  const coursesForStepFilter = React.useMemo(() => getFilteredCourses('step'), [getFilteredCourses]);
  const coursesForLabelFilter = React.useMemo(() => getFilteredCourses('label'), [getFilteredCourses]);
  const coursesForCommentFilter = React.useMemo(() => getFilteredCourses('comment'), [getFilteredCourses]);
  const coursesForSegmentFilter = React.useMemo(() => getFilteredCourses('segment'), [getFilteredCourses]);
  const coursesForVersionFilter = React.useMemo(() => getFilteredCourses('version'), [getFilteredCourses]);
  const coursesForArchivedFilter = React.useMemo(() => getFilteredCourses('archived'), [getFilteredCourses]);

  // Function to clear all filters
  const clearAllFilters = () => {
    setFilterText('');
    setFilterStep(undefined);
    setFilterLabel(undefined);
    setFilterVersion(undefined);
    setFilterComment(undefined);
    setFilterProviderId(undefined);
    setFilterExpertId(undefined);
    setFilterSegment(undefined);
    setFilterArchived(undefined);
    setFilterYear(undefined);
  };

  // Check if any filters are active
  const hasActiveFilters = React.useMemo(() => {
    return !!(
      filterText ||
      (filterStep?.length && filterStep.length > 0) ||
      (filterLabel?.length && filterLabel.length > 0) ||
      (filterVersion?.length && filterVersion.length > 0) ||
      (fitlerComment?.length && fitlerComment.length > 0) ||
      (filterProviderId?.length && filterProviderId.length > 0) ||
      (filterExpertId?.length && filterExpertId.length > 0) ||
      (filterSegment?.length && filterSegment.length > 0) ||
      (filterArchived?.length && filterArchived.length > 0) ||
      (filterYear?.length && filterYear.length > 0)
    );
  }, [filterText, filterStep, filterLabel, filterVersion, fitlerComment, filterProviderId, filterExpertId, filterSegment, filterArchived, filterYear]);

  return (
    <>
      <AppToolbar>
        <HStack>
          {AuthService.isStaff && <Button hideBelow='md' variant='ghost' isDisabled={isLoading || !!props.errorMessage} onClick={() => setNewCoursePlatform(PLATFORM_VERSION.V2)} size='md'>Add New Course...</Button>}
          <IconButton hideBelow='md' onClick={() => loadCourses(true)} aria-label="Refresh" isDisabled={isLoading} variant='ghost' icon={<Icon as={FiRefreshCw} />} />
          <Menu>
            <MenuButton as={IconButton} isDisabled={isLoading} variant='ghost' icon={<FiMenu />}>Actions</MenuButton>
            <MenuList>
              <MenuItem icon={<Icon as={FiEye} />} onClick={() => setIsColumnSidebarOpen(true)} isDisabled={isLoading}>Manage Columns</MenuItem>
              <MenuItem hideFrom='md' isDisabled={isLoading || !!props.errorMessage} onClick={() => loadCourses(true)}>Refresh</MenuItem>
              {AuthService.isStaff && <MenuItemOpenSharePoint relativeUrl='Courses' />}
              {AuthService.isStaff && <MenuItem hideFrom='md' isDisabled={isLoading || !!props.errorMessage} onClick={() => setNewCoursePlatform(PLATFORM_VERSION.V2)}>Add New Course...</MenuItem>}
              {AuthService.isStaff && <MenuItem isDisabled={isLoading || !!props.errorMessage} onClick={() => setNewCoursePlatform(PLATFORM_VERSION.V1)}>Add New Course (V1)...</MenuItem>}
            </MenuList>
          </Menu>
        </HStack>
      </AppToolbar>
      <Box opacity={isLoading ? 0.5 : undefined} maxW='full'>
        <Stack>
          <Container>
            <HStack justify="space-between">
              <HStack spacing="2" justify='start'>
                <InputGroup maxW="xs">
                  <InputLeftElement pointerEvents="none">
                    <Icon as={FiSearch} color="fg.muted" boxSize="5" />
                  </InputLeftElement>
                  <Input placeholder="Search" value={filterText} onChange={evt => setFilterText(evt.target.value)} />
                </InputGroup>
                <Divider hideBelow='md' orientation='vertical' />
                <FilterBox isDisabled={isLoading} onClearAllFilters={clearAllFilters} hasActiveFilters={hasActiveFilters}>
                  <CheckboxFilterYear allCourses={coursesForYearFilter} value={filterYear} onSetFilter={setFilterYear} />
                  <CheckboxFilterProvider allCourses={coursesForProviderFilter} value={filterProviderId} onSetFilter={setFilterProviderId} />
                  <CheckboxFilterExpert allCourses={coursesForExpertFilter} value={filterExpertId} onSetFilter={setFilterExpertId} />
                  <CheckboxFilterStepStatus allCourses={coursesForStepFilter} stepValue={filterStep} onSetStepFilter={setFilterStep} />
                  <CheckboxFilterLabel allCourses={coursesForLabelFilter} value={filterLabel} onSetFilter={setFilterLabel} />
                  <CheckboxFilterComments allCourses={coursesForCommentFilter} value={fitlerComment} onSetFilter={setFilterComment} />
                  <CheckboxFilterSegment allCourses={coursesForSegmentFilter} value={filterSegment} onSetFilter={setFilterSegment} />
                  <CheckboxFilterVersion allCourses={coursesForVersionFilter} value={filterVersion} onSetFilter={setFilterVersion} />
                  <CheckboxFilterArchived allCourses={coursesForArchivedFilter} value={filterArchived} onSetFilter={setFilterArchived} />
                </FilterBox>
              </HStack>
              <TableCounterBox isLoading={isLoading} count={courses.length} label='course(s)' />
            </HStack>
          </Container>
          <Box overflowX="auto">
            <CourseTable
              filter={filterWords}
              courses={courses}
              onUpdateCourse={item => setCourseToUpdate(item)}
              onDeleteCourse={item => setCourseToDelete(item)}
              onInviteExpert={item => setCourseToInviteExpert(item)}
              onNewRepeatedCourse={item => setCourseToCreateNewRepeated(item)}
              onRemindProvider={item => setCourseToRemindProvider(item)}
              onCourseDeadlines={item => setCourseToEditDeadlines(item)}
              onArchiveCourse={onCourseArchived}
              columnVisibility={columnVisibility}
              onColumnVisibilityChange={setColumnVisibility}
              columnOrder={columnOrder}
              onColumnOrderChange={setColumnOrder}
            />
          </Box>
        </Stack>
        {newCoursePlatform && <NewCourseDialog platformVersion={newCoursePlatform} onClose={() => setNewCoursePlatform(undefined)} isDisabled={isLoading || !!props.errorMessage} />}
        {courseToEdit && <EditCourseDialog course={courseToEdit} onClose={() => setCourseToUpdate(undefined)} onCourseUpdated={onCourseUpdated} />}
        {courseToDelete && <DeleteCourseDialog course={courseToDelete} onClose={() => setCourseToDelete(undefined)} onCourseDeleted={onCourseDeleted} />}
        {courseToCreateNewRepeated && <NewRepeatedCourseDialog course={courseToCreateNewRepeated} onClose={() => setCourseToCreateNewRepeated(undefined)} onRepeatedCourseCreated={onRepeatedCourseCreated} />}
        {courseToInviteExpert && <AssignToExpertDialog course={courseToInviteExpert} onClose={() => setCourseToInviteExpert(undefined)} onExpertChanged={onCourseExpertInvited} />}
        {courseToRemindProvider && <RemindProviderDialog course={courseToRemindProvider} onDismiss={() => setCourseToRemindProvider(undefined)} onSendReminder={onSendReminder} />}
        {courseToEditDeadlines && <EditDeadlineDialog course={courseToEditDeadlines} onCourseDeadlinesChanged={onCourseUpdated} onClose={() => setCourseToEditDeadlines(undefined)} />}
        <ColumnVisibilitySidebar
          isOpen={isColumnSidebarOpen}
          onClose={() => setIsColumnSidebarOpen(false)}
          columns={CourseColumnDefinitions}
          columnVisibility={columnVisibility}
          onColumnVisibilityChange={setColumnVisibility}
          columnOrder={columnOrder}
          onColumnOrderChange={setColumnOrder}
        />
      </Box>
    </>
  )
}